import pb from './pocketbase.js';

/** collection::topic → Set of callbacks (multiple listeners OK) */
const listenerSets = new Map();
/** collection::topic → { collection, topic } for rebind */
const subs = new Map();

function key(collection, topic) {
  return `${collection}::${topic || '*'}`;
}

async function dispatchAll(k, event) {
  const set = listenerSets.get(k);
  if (!set) return;
  for (const cb of [...set]) {
    try {
      await cb(event);
    } catch (err) {
      console.warn(`Realtime listener error (${k}):`, err);
    }
  }
}

export async function unsubscribeSafe(collection, topic = '*') {
  try {
    await pb.collection(collection).unsubscribe(topic);
  } catch {
    /* ignore */
  }
}

/**
 * Subscribe without clobbering other listeners on the same collection.
 * PocketBase only keeps one handler per topic — we multiplex.
 */
export async function subscribeSafe(collection, topic, callback, retries = 3) {
  if (typeof callback !== 'function') return null;

  const k = key(collection, topic);
  const topicName = topic || '*';

  if (!listenerSets.has(k)) listenerSets.set(k, new Set());
  listenerSets.get(k).add(callback);
  subs.set(k, { collection, topic: topicName });

  if (!pb.authStore.isValid) {
    console.warn(`Realtime skip (${collection}): auth not valid yet`);
    return () => removeListener(collection, topicName, callback);
  }

  // Already connected with multiplex — just added callback
  if (listenerSets.get(k).size > 1) {
    return () => removeListener(collection, topicName, callback);
  }

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      await unsubscribeSafe(collection, topicName);
      await pb.collection(collection).subscribe(topicName, (e) => dispatchAll(k, e));
      return () => removeListener(collection, topicName, callback);
    } catch (err) {
      console.warn(`Realtime subscribe failed (${collection}) attempt ${attempt}:`, err);
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, 300 * attempt));
      }
    }
  }
  return () => removeListener(collection, topicName, callback);
}

async function removeListener(collection, topic, callback) {
  const k = key(collection, topic);
  const set = listenerSets.get(k);
  if (set) {
    set.delete(callback);
    if (set.size === 0) {
      listenerSets.delete(k);
      subs.delete(k);
      await unsubscribeSafe(collection, topic);
    }
  }
}

export async function removeSubscription(collection, topic = '*') {
  const k = key(collection, topic);
  listenerSets.delete(k);
  subs.delete(k);
  await unsubscribeSafe(collection, topic);
}

/** Re-bind all active subscriptions (after auth change or dead socket). */
export async function ensureSubscriptions() {
  if (!pb.authStore.isValid) return;
  for (const [k, { collection, topic }] of subs.entries()) {
    if (!listenerSets.get(k)?.size) continue;
    try {
      await unsubscribeSafe(collection, topic);
      await pb.collection(collection).subscribe(topic, (e) => dispatchAll(k, e));
    } catch (err) {
      console.warn(`Realtime rebind failed (${collection}):`, err);
    }
  }
}

export function onAuthChanged(handler) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('pb-auth-changed', handler);
  return () => window.removeEventListener('pb-auth-changed', handler);
}

if (typeof window !== 'undefined') {
  onAuthChanged(() => {
    ensureSubscriptions();
  });
  window.addEventListener('online', () => {
    ensureSubscriptions();
  });
}
